import { useEffect, useState } from "react";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import { toast } from "sonner";
import axios, { API, getErrorMessage } from "@/lib/api";
import LoadingScreen from "@/components/shared/LoadingScreen";

export default function AdminPengaturan() {
  const [form, setForm] = useState(null);
  const [saving, setSaving] = useState(false);

  useEffect(() => {
    axios.get(`${API}/public/settings`).then(({ data }) => setForm(data)).catch(() => {});
  }, []);

  if (!form) return <LoadingScreen />;

  const update = (key, value) => setForm((f) => ({ ...f, [key]: value }));
  const updateStats = (key, value) => setForm((f) => ({ ...f, stats: { ...f.stats, [key]: value } }));
  const updateSocial = (key, value) => setForm((f) => ({ ...f, social_links: { ...f.social_links, [key]: value } }));

  const handleSave = async () => {
    setSaving(true);
    try {
      await axios.put(`${API}/admin/pengaturan`, form);
      toast.success("Pengaturan berhasil disimpan");
    } catch (e) {
      toast.error(getErrorMessage(e));
    } finally {
      setSaving(false);
    }
  };

  return (
    <div data-testid="admin-pengaturan-page" className="max-w-3xl space-y-6">
      <div>
        <h1 className="font-heading text-2xl font-bold text-[#1F2937]">Pengaturan Website</h1>
        <p className="text-sm text-[#64748B]">Kelola informasi dan konten yang tampil di website publik.</p>
      </div>

      <div className="space-y-4 rounded-xl border border-slate-100 bg-white p-6">
        <h3 className="font-heading text-sm font-semibold text-[#1F2937]">Informasi Organisasi</h3>
        <div><Label>Nama Organisasi</Label><Input data-testid="settings-org-name" value={form.org_name || ""} onChange={(e) => update("org_name", e.target.value)} /></div>
        <div><Label>Deskripsi</Label><Textarea data-testid="settings-description" value={form.description || ""} onChange={(e) => update("description", e.target.value)} rows={2} /></div>
        <div><Label>Alamat</Label><Input data-testid="settings-address" value={form.address || ""} onChange={(e) => update("address", e.target.value)} /></div>
        <div className="grid grid-cols-2 gap-3">
          <div><Label>Telepon</Label><Input data-testid="settings-phone" value={form.phone || ""} onChange={(e) => update("phone", e.target.value)} /></div>
          <div><Label>WhatsApp (format: 62xxxx)</Label><Input data-testid="settings-whatsapp" value={form.whatsapp || ""} onChange={(e) => update("whatsapp", e.target.value)} /></div>
        </div>
        <div><Label>Email</Label><Input data-testid="settings-email" value={form.email || ""} onChange={(e) => update("email", e.target.value)} /></div>
        <div><Label>Jam Operasional</Label><Input data-testid="settings-service-hours" value={form.service_hours || ""} onChange={(e) => update("service_hours", e.target.value)} /></div>
        <div><Label>URL Google Maps</Label><Input data-testid="settings-maps-url" value={form.maps_url || ""} onChange={(e) => update("maps_url", e.target.value)} /></div>
      </div>

      <div className="space-y-4 rounded-xl border border-slate-100 bg-white p-6">
        <h3 className="font-heading text-sm font-semibold text-[#1F2937]">Hero Section</h3>
        <div><Label>URL Gambar Hero</Label><Input data-testid="settings-hero-image" value={form.hero_image || ""} onChange={(e) => update("hero_image", e.target.value)} /></div>
        <div><Label>Judul Hero</Label><Textarea data-testid="settings-hero-title" value={form.hero_title || ""} onChange={(e) => update("hero_title", e.target.value)} rows={2} /></div>
        <div><Label>Sub Judul Hero</Label><Textarea data-testid="settings-hero-subtitle" value={form.hero_subtitle || ""} onChange={(e) => update("hero_subtitle", e.target.value)} rows={2} /></div>
      </div>

      <div className="space-y-4 rounded-xl border border-slate-100 bg-white p-6">
        <h3 className="font-heading text-sm font-semibold text-[#1F2937]">Statistik Beranda</h3>
        <div className="grid grid-cols-2 gap-3">
          <div><Label>Jamaah Dibina</Label><Input data-testid="settings-stat-jamaah" value={form.stats?.jamaah || ""} onChange={(e) => updateStats("jamaah", e.target.value)} /></div>
          <div><Label>Program Manasik</Label><Input data-testid="settings-stat-program" value={form.stats?.program || ""} onChange={(e) => updateStats("program", e.target.value)} /></div>
          <div><Label>Pembimbing</Label><Input data-testid="settings-stat-pembimbing" value={form.stats?.pembimbing || ""} onChange={(e) => updateStats("pembimbing", e.target.value)} /></div>
          <div><Label>Tahun Pengalaman</Label><Input data-testid="settings-stat-tahun" value={form.stats?.tahun || ""} onChange={(e) => updateStats("tahun", e.target.value)} /></div>
        </div>
      </div>

      <div className="space-y-4 rounded-xl border border-slate-100 bg-white p-6">
        <h3 className="font-heading text-sm font-semibold text-[#1F2937]">Media Sosial</h3>
        <div className="grid grid-cols-1 gap-3 sm:grid-cols-3">
          <div><Label>Instagram</Label><Input data-testid="settings-social-instagram" value={form.social_links?.instagram || ""} onChange={(e) => updateSocial("instagram", e.target.value)} /></div>
          <div><Label>Facebook</Label><Input data-testid="settings-social-facebook" value={form.social_links?.facebook || ""} onChange={(e) => updateSocial("facebook", e.target.value)} /></div>
          <div><Label>Youtube</Label><Input data-testid="settings-social-youtube" value={form.social_links?.youtube || ""} onChange={(e) => updateSocial("youtube", e.target.value)} /></div>
        </div>
      </div>

      <div className="space-y-4 rounded-xl border border-slate-100 bg-white p-6">
        <h3 className="font-heading text-sm font-semibold text-[#1F2937]">Halaman Tentang Kami</h3>
        <div><Label>Judul Hero</Label><Input data-testid="settings-about-hero-title" value={form.about_hero_title || ""} onChange={(e) => update("about_hero_title", e.target.value)} /></div>
        <div><Label>Sub Judul Hero</Label><Textarea data-testid="settings-about-hero-subtitle" value={form.about_hero_subtitle || ""} onChange={(e) => update("about_hero_subtitle", e.target.value)} rows={2} /></div>
        <div><Label>URL Gambar Hero</Label><Input data-testid="settings-about-hero-image" value={form.about_hero_image || ""} onChange={(e) => update("about_hero_image", e.target.value)} /></div>
        <div><Label>Judul Sejarah</Label><Input data-testid="settings-about-history-title" value={form.about_history_title || ""} onChange={(e) => update("about_history_title", e.target.value)} /></div>
        <div><Label>Isi Sejarah (pisahkan paragraf dengan baris kosong)</Label><Textarea data-testid="settings-about-history-content" value={form.about_history_content || ""} onChange={(e) => update("about_history_content", e.target.value)} rows={5} /></div>
        <div><Label>URL Gambar Sejarah</Label><Input data-testid="settings-about-history-image" value={form.about_history_image || ""} onChange={(e) => update("about_history_image", e.target.value)} /></div>
        <div><Label>Visi</Label><Textarea data-testid="settings-about-vision" value={form.about_vision || ""} onChange={(e) => update("about_vision", e.target.value)} rows={2} /></div>
        <div><Label>Misi</Label><Textarea data-testid="settings-about-mission" value={form.about_mission || ""} onChange={(e) => update("about_mission", e.target.value)} rows={2} /></div>
      </div>

      <div className="space-y-4 rounded-xl border border-slate-100 bg-white p-6">
        <h3 className="font-heading text-sm font-semibold text-[#1F2937]">Iklan Halaman Artikel</h3>
        <p className="text-xs text-[#64748B]">Gambar iklan akan tampil di sisi kiri &amp; kanan halaman detail artikel (tampil di layar besar).</p>
        <div className="grid grid-cols-1 gap-4 sm:grid-cols-2">
          <div className="space-y-2 rounded-lg border border-dashed border-slate-200 p-3">
            <Label>URL Gambar Iklan Kiri</Label>
            <Input data-testid="settings-ad-left-image" value={form.ad_left_image || ""} onChange={(e) => update("ad_left_image", e.target.value)} />
            <Label>Link Tujuan Iklan Kiri (Opsional)</Label>
            <Input data-testid="settings-ad-left-link" value={form.ad_left_link || ""} onChange={(e) => update("ad_left_link", e.target.value)} />
          </div>
          <div className="space-y-2 rounded-lg border border-dashed border-slate-200 p-3">
            <Label>URL Gambar Iklan Kanan</Label>
            <Input data-testid="settings-ad-right-image" value={form.ad_right_image || ""} onChange={(e) => update("ad_right_image", e.target.value)} />
            <Label>Link Tujuan Iklan Kanan (Opsional)</Label>
            <Input data-testid="settings-ad-right-link" value={form.ad_right_link || ""} onChange={(e) => update("ad_right_link", e.target.value)} />
          </div>
        </div>
      </div>

      <Button data-testid="admin-settings-save-button" disabled={saving} onClick={handleSave} className="h-11 w-full rounded-full bg-gradient-to-r from-[#0F6B4F] to-[#1677B8] text-white hover:opacity-90">
        {saving ? "Menyimpan..." : "Simpan Pengaturan"}
      </Button>
    </div>
  );
}
